import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { CreateNewSessionAndGetChatBotRoot, GetNodeChildsList } from '../../Controller/Api/ChatBotController';
import { ChannelEnum, CreateNewSessionAndGetChatBotRootRequest } from '../../Model/Request/ChatBot/CreateNewSessionAndGetChatBotRootRequest';
import { ChatBotNode, GetNodeChildsListResponse, NodeType } from '../../Model/Response/ChatBot/GetNodeChildsListResponse';
import { ApiBaseResponse, ResponseCode } from '../../Model/Response/Common/ApiBaseResponse';
import { FaHome, FaArrowLeft } from 'react-icons/fa';
import './ChatBotMainScreen.css'


const createLinks = (text: string) => {
    if (text == null) return;

    // URL'leri tanımlayan regex: http, https veya www ile başlayanlar
    const urlRegex = /((https?:\/\/|www\.)[^\s]+)/g;
    const emailRegex = /([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9._-]+)/g;

    // \n karakterlerine göre bölerek her satırı işliyoruz
    return text.split('\n').map((line, lineIndex) => (
        <div key={lineIndex} style={{ margin: '0 0 10px 0', textAlign: 'start' }}>
            {line.split(' ').map((word, wordIndex) => {
                if (urlRegex.test(word)) {
                    // Eğer www ile başlıyorsa başına http ekliyoruz
                    const href = word.startsWith('www.') ? `http://${word}` : word;
                    return (
                        <a
                            key={wordIndex}
                            href={href}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: '#2980b9', textDecoration: 'underline', marginRight: '5px' }}
                        >
                            {word}
                        </a>
                    );
                } else if (emailRegex.test(word)) {
                    return (
                        <a
                            key={wordIndex}
                            href={`mailto:${word}`}
                            style={{ color: '#2980b9', textDecoration: 'underline', marginRight: '5px' }}
                        >
                            {word}
                        </a>
                    );
                } else {
                    return word + ' ';
                }
            })}
        </div>
    ));
};





const ChatBotMainScreen: React.FC = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate(); // Yönlendirme için navigate kullanıyoruz
    const id = searchParams.get('id');
    const guid = searchParams.get('guid');
    const { lang } = useParams(); // useParams ile lang parametresini aldık

    const [nodes, setNodes] = useState<ChatBotNode[]>([]);
    const [history, setHistory] = useState<ChatBotNode[][]>([]);
    const [sessionId, setSessionId] = useState<number | null>(null);
    const [sessionToken, setSessionToken] = useState<string | null>(null);
    const [companyName, setCompanyName] = useState<string>('');
    const [selectedContent, setSelectedContent] = useState<React.ReactNode | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const [liveSupportSystemId, setLiveSupportSystemId] = useState<number | null>(null);
    const [liveSupportSystemToken, setLiveSupportSystemToken] = useState<string | null>(null);

    useEffect(() => {
        if (id && guid && lang) {
            fetchRootNodes(parseInt(id), guid, lang);
        }
    }, [id, guid, lang]);

    useEffect(() => {
        if (companyName) {
            document.title = companyName; // Sayfa başlığı olarak companyName yazdır
        }
    }, [companyName]);

    const fetchRootNodes = async (chatBotId: number, chatBotToken: string, lang: string) => {
        try {
            const request: CreateNewSessionAndGetChatBotRootRequest = {
                chatBotId: chatBotId,
                chatBotToken: chatBotToken,
                lang: lang,
                channel: ChannelEnum.webPage
            };

            const response: ApiBaseResponse<GetNodeChildsListResponse> | null =
                await CreateNewSessionAndGetChatBotRoot(request);

            if (!response || !response.content || response.responseCode !== ResponseCode.OK) {
                // TODO: Hata mesajı göster
                return;
            }

            setSessionId(response.content.sessionId);
            setSessionToken(response.content.sessionToken);
            setNodes(response.content.nodes);
            setCompanyName(response.content.companyName); // companyName state'e aktar
            setHistory([]); // Home'a dönmek için geçmişi temizle
            setSelectedContent(null);
            setLiveSupportSystemId(response.content.liveSupportSystemId);
            setLiveSupportSystemToken(response.content.liveSupportSystemToken);
        } catch (error) {
            console.error('Root nodes fetch error:', error);
        }
    };

    const fetchChildNodes = async (nodeId: number) => {
        if (!sessionId || !sessionToken) return;

        setIsLoading(true); // Animasyon başlangıcı
        try {
            const header = `${sessionId}_${sessionToken}`;

            const response: ApiBaseResponse<GetNodeChildsListResponse> | null =
                await GetNodeChildsList(header, nodeId);

            if (!response || !response.content || response.responseCode !== ResponseCode.OK) {
                // TODO: Hata mesajı göster
                return;
            }

            setHistory((prevHistory) => [...prevHistory, nodes]); // Mevcut node'ları geçmişe ekle
            setNodes(response.content.nodes);


            // Eğer gelen dataların boyu 1 ve bu node content ise selectedContent'i işlenmiş şekilde ayarla
            if (response.content.nodes.length === 1 && response.content.nodes[0].nodeType === NodeType.Content) {
                const processedContent = createLinks(response.content.nodes[0].nodeContent);
                setSelectedContent(processedContent); // Artık React elementleri atanabilir
            } else {
                setSelectedContent(null);
            }
        } catch (error) {
            console.error('Child nodes fetch error:', error);
        } finally {
            setTimeout(() => setIsLoading(false), 300); // Animasyon bitişi
        }
    };

    const handleNodeClick = (node: ChatBotNode) => {
        if (node.nodeType === NodeType.Content) {
            // Eğer içerik tipi bir node ise, doğrudan "Son Bilgi" olarak göster.
            setSelectedContent(node.nodeContent);
        } else {
            fetchChildNodes(node.nodeId);
        }
    };

    const handleBackClick = () => {
        if (history.length > 0) {
            const previousNodes = history[history.length - 1];
            setHistory((prevHistory) => prevHistory.slice(0, -1)); // Son geçmişi çıkar
            setNodes(previousNodes);
            setSelectedContent(null);
        }
    };

    const handleHomeClick = () => {
        if (id && guid && lang) {
            fetchRootNodes(parseInt(id), guid, lang); // Ana ekrana dön
        }
    };


    const handleLiveSupportClick = () => {
        if (liveSupportSystemId && liveSupportSystemToken && lang && sessionId && sessionToken) {
            const url = `/${lang}/WebChat?guid=${liveSupportSystemToken}&hId=${liveSupportSystemId}&botId=${sessionId}&botToken=${sessionToken}`;
            navigate(url);
        }
    };


    return (
        <div
            style={{
                padding: '20px',
                width: '24rem',
                height: '38rem',
                margin: '0 auto',
                overflow: 'hidden', // Dış taşmaları önler
            }}
        >
            <div
                style={{
                    border: '1px solid #ccc',
                    borderRadius: '8px',
                    padding: '20px',
                    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    backgroundColor: 'white',
                    overflowY: 'auto', // Taşma durumunda dikey scroll çıkar
                    position: 'relative',
                }}
            >
                {/* Üstte Firma Adı Alanı */}
                <div
                    style={{
                        padding: '10px 20px',
                        backgroundColor: '#2c3e50',
                        color: 'white',
                        textAlign: 'center',
                        borderRadius: '8px 8px 0 0',
                        marginBottom: '10px',
                    }}
                >
                    <h3 style={{ margin: 0 }}>{companyName}</h3>
                </div>

                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        marginBottom: '10px',
                        position: 'relative',
                    }}
                >
                    <div style={{ position: 'relative' }}>
                        <button
                            onClick={handleBackClick}
                            style={{
                                padding: '10px',
                                backgroundColor: '#f39c12',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                visibility: history.length > 0 ? 'visible' : 'hidden',
                            }}
                        >
                            <FaArrowLeft />
                        </button>
                        {history.length > 0 && (
                            <span
                                style={{
                                    position: 'absolute',
                                    bottom: '-25px',
                                    left: '50%',
                                    transform: 'translateX(-50%)',
                                    backgroundColor: '#000',
                                    color: '#fff',
                                    padding: '5px',
                                    borderRadius: '4px',
                                    fontSize: '12px',
                                    whiteSpace: 'nowrap',
                                    visibility: 'hidden',
                                    opacity: 0,
                                    transition: 'opacity 0.2s ease-in-out',
                                }}
                                className="tooltip-back"
                            >
                                Geri
                            </span>
                        )}
                    </div>
                    <div style={{ position: 'relative' }}>
                        <button
                            onClick={handleHomeClick}
                            style={{
                                padding: '10px',
                                backgroundColor: '#27ae60',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                            }}
                        >
                            <FaHome />
                        </button>
                        <span
                            style={{
                                position: 'absolute',
                                bottom: '-25px',
                                left: '50%',
                                transform: 'translateX(-50%)',
                                backgroundColor: '#000',
                                color: '#fff',
                                padding: '5px',
                                borderRadius: '4px',
                                fontSize: '12px',
                                whiteSpace: 'nowrap',
                                visibility: 'hidden',
                                opacity: 0,
                                transition: 'opacity 0.2s ease-in-out',
                            }}
                            className="tooltip-home"
                        >
                            Ana Sayfa
                        </span>
                    </div>
                </div>

                {selectedContent ? (
                    <div
                        style={{
                            padding: '20px',
                            backgroundColor: '#fff',
                            borderRadius: '8px',
                            boxShadow: '0 4px 8px rgba(0,0,0,0.1)', 
                            overflowY: 'auto',
                        }}
                         className="scroll-container"
                    >
                        <h3> {/* Son Bilgi */}</h3>
                        <p
                            style={{
                                textAlign: 'justify', // Metni justify yapar
                                margin: 0, // Varsayılan boşlukları sıfırlar
                            }}
                        >
                            {selectedContent}
                        </p>
                        {/* Canlı Destek Mesajı */}
                        {liveSupportSystemId && liveSupportSystemToken && (
                            <div
                                style={{
                                    marginTop: '20px',
                                    paddingTop: '10px',
                                    borderTop: '1px solid #ccc',
                                    textAlign: 'center',
                                }}
                            >
                                <p
                                    style={{
                                        color: '#2980b9',
                                        cursor: 'pointer',
                                        fontWeight: 'bold',
                                    }}
                                    onClick={handleLiveSupportClick}
                                >
                                    İhtiyacınızı çözemediniz mi? Canlı desteğe bağlanmak ister misiniz?
                                </p>
                            </div>
                        )}
                    </div>
                ) : (
                    <div
                        className="scroll-container"
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            overflowY: 'auto',
                            gap: '10px',
                            opacity: isLoading ? 0.5 : 1,
                            transition: 'opacity 0.3s ease-in-out',
                        }}
                    >
                        <div
                            className="scroll-container" // Scroll bar için özelleştirilmiş CSS sınıfı
                            style={{
                                border: '1px solid #ccc',
                                borderRadius: '8px', // Dış border köşeleri yuvarlatılır
                                overflow: 'hidden', // Köşelerden taşmayı önler
                                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)', // Hafif bir gölge efekti
                                maxHeight: '400px', // Maksimum yükseklik ayarlanır
                                overflowY: 'auto', // İçerik taştığında dikey kaydırma çubuğu eklenir
                            }}
                        >
                            {nodes.map((node, index) => (
                                <React.Fragment key={node.nodeId}>
                                    <div
                                        onClick={() => handleNodeClick(node)}
                                        style={{
                                            padding: '12px',
                                            backgroundColor: '#f9f9f9', // Hafif bir arka plan rengi
                                            cursor: 'pointer',
                                            textAlign: 'left',
                                            transition: 'background-color 0.3s ease',
                                        }}
                                        onMouseOver={(e) => (e.currentTarget.style.backgroundColor = '#f0f0f0')}
                                        onMouseOut={(e) => (e.currentTarget.style.backgroundColor = '#f9f9f9')}
                                    >
                                        {node.nodeType === NodeType.Content
                                            ? createLinks(node.nodeContent)
                                            : node.nodeContent}
                                    </div>
                                    {index < nodes.length - 1 && (
                                        <div
                                            style={{
                                                height: '1px',
                                                backgroundColor: '#ccc', // Çizgi rengi
                                                margin: '0',
                                            }}
                                        ></div>
                                    )}
                                </React.Fragment>
                            ))}
                        </div>

                    </div>
                )}
            </div>
        </div>
    );
};

export default ChatBotMainScreen;
